import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency, formatDate } from '@/lib/utils';

export const GovtDashboardTab = ({ loading, dashboard, services, deptIcons, statusColors }) => {
  if (loading) return <div className="text-center py-12">Loading dashboard...</div>;
  if (!dashboard) return null;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-600">{dashboard.summary.total_applications}</p>
            <p className="text-sm text-muted-foreground">Total Applications</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-amber-600">{dashboard.summary.pending_applications}</p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-emerald-600">{dashboard.summary.completed_this_month}</p>
            <p className="text-sm text-muted-foreground">Completed (Month)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-teal-600">{formatCurrency(dashboard.summary.total_govt_fees)}</p>
            <p className="text-sm text-muted-foreground">Govt Fees</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-violet-600">{formatCurrency(dashboard.summary.total_service_fees)}</p>
            <p className="text-sm text-muted-foreground">Service Fees</p>
          </CardContent>
        </Card>
      </div>

      {/* By Department */}
      <Card>
        <CardHeader>
          <CardTitle>Applications by Department</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['ICP', 'MOHRE', 'EID', 'TASHEEL'].map((dept) => {
              const Icon = deptIcons[dept];
              const data = dashboard.by_department.find(d => d.department === dept) || { count: 0, total_fees: 0 };
              return (
                <div key={dept} className="p-4 rounded-lg bg-slate-50 border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-white shadow-sm">
                      <Icon className="w-5 h-5 text-slate-600" />
                    </div>
                    <span className="font-semibold">{services[dept]?.name || dept}</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-2xl font-bold">{data.count}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatCurrency(data.total_fees)} in fees
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Status Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Status Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(dashboard.by_status || {}).map(([status, count]) => (
              <Badge key={status} className={`${statusColors[status]} text-sm py-1 px-3`}>
                {status}: {count}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Applications */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Applications</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboard.recent_applications?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Application #</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboard.recent_applications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell className="font-mono">{app.application_number}</TableCell>
                    <TableCell>{app.department_name}</TableCell>
                    <TableCell>{app.service_name}</TableCell>
                    <TableCell>{app.applicant_name}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[app.status]}>{app.status}</Badge>
                    </TableCell>
                    <TableCell>{formatDate(app.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center py-8 text-muted-foreground">No applications yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
